/**
 * Firebase Auth session for the Cognitory landing shell.
 * Google popup sign-in only. No Firestore, no tokens printed, and no
 * client-side Cognitory authorization decisions (access is backend-resolved).
 */

import { useCallback, useEffect, useState } from "react";
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type User,
} from "firebase/auth";
import { getCognitoryAuth } from "../firebase/firebaseApp";
import {
  buildBoundedIdentityDiagnostics,
  type BoundedIdentityDiagnostics,
} from "./identityDiagnostics";

export type CognitoryAuthStatus =
  | "loading"
  | "signed_out"
  | "signed_in"
  | "error";

export type CognitoryAuthState = {
  status: CognitoryAuthStatus;
  email: string | null;
  diagnostics: BoundedIdentityDiagnostics | null;
  errorMessage: string | null;
  signIn: () => Promise<void>;
  signOutUser: () => Promise<void>;
  clearError: () => void;
};

const googleProvider = new GoogleAuthProvider();

function mapAuthError(error: unknown): string {
  const code =
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    typeof (error as { code: unknown }).code === "string"
      ? (error as { code: string }).code
      : "";

  if (
    code === "auth/popup-closed-by-user" ||
    code === "auth/cancelled-popup-request"
  ) {
    return "Sign-in was cancelled.";
  }
  if (code === "auth/network-request-failed") {
    return "Sign-in could not reach Firebase Authentication.";
  }
  if (code === "auth/unauthorized-domain") {
    return "This domain is not authorized for Firebase sign-in.";
  }
  return "Sign-in failed. Try again.";
}

async function diagnosticsForUser(
  user: User,
): Promise<BoundedIdentityDiagnostics> {
  return buildBoundedIdentityDiagnostics({
    email: user.email,
    uid: user.uid,
  });
}

export function useCognitoryAuth(): CognitoryAuthState {
  const [status, setStatus] = useState<CognitoryAuthStatus>("loading");
  const [email, setEmail] = useState<string | null>(null);
  const [diagnostics, setDiagnostics] =
    useState<BoundedIdentityDiagnostics | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    try {
      const auth = getCognitoryAuth();
      unsubscribe = onAuthStateChanged(
        auth,
        async user => {
          if (cancelled) return;
          if (!user || user.isAnonymous) {
            setStatus("signed_out");
            setEmail(null);
            setDiagnostics(null);
            return;
          }
          try {
            const next = await diagnosticsForUser(user);
            if (cancelled) return;
            setEmail(next.email || null);
            setDiagnostics(next);
            setStatus("signed_in");
            setErrorMessage(null);
          } catch {
            if (cancelled) return;
            setStatus("error");
            setEmail(null);
            setDiagnostics(null);
            setErrorMessage("Could not prepare identity diagnostics.");
          }
        },
        () => {
          if (cancelled) return;
          setStatus("error");
          setEmail(null);
          setDiagnostics(null);
          setErrorMessage("Firebase Authentication failed to initialize.");
        },
      );
    } catch {
      setStatus("error");
      setErrorMessage("Firebase Authentication failed to initialize.");
    }

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  const signIn = useCallback(async () => {
    setErrorMessage(null);
    try {
      await signInWithPopup(getCognitoryAuth(), googleProvider);
    } catch (error) {
      setErrorMessage(mapAuthError(error));
    }
  }, []);

  const signOutUser = useCallback(async () => {
    setErrorMessage(null);
    try {
      await signOut(getCognitoryAuth());
    } catch {
      setErrorMessage("Sign-out failed. Try again.");
    }
  }, []);

  const clearError = useCallback(() => setErrorMessage(null), []);

  return {
    status,
    email,
    diagnostics,
    errorMessage,
    signIn,
    signOutUser,
    clearError,
  };
}
